import { normalizeLexiconTrigramText } from './lexicon-trigram'
import { ErrorDetails } from '@w/ui/components/error-details'
import { useRouterContext, useParams } from '../router/flashcards-fe.router-bindings'
import { setSenseLearning } from './sense-learning'
import { localStorageOrMemory, useEditorKeys } from '@w/utils-fe'
import { useRef, useState } from 'react'
import { AudioPlayer } from '@w/react-audio'
import { ulid } from '@w/utils'
import { Button } from '@w/ui/components/button'
import { Fab } from '@w/ui/components/fab'
import { Checkbox } from '@w/ui/components/checkbox'
import { Input } from '@w/ui/components/input'
import { Image } from '@w/ui/components/image'
import { IconButton } from '@w/ui/components/icon-button'
import {
  BookMarkedIcon,
  ChevronDownIcon,
  PlusIcon,
  Trash2Icon,
  XIcon,
} from '@w/ui/components/icon'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@w/ui/components/menu'
import { Tooltip, TooltipPopup, TooltipTrigger } from '@w/ui/components/tooltip'
import type {
  CachedEntry,
  Cefr,
  Example,
  TranslationLanguage,
  LexiconEntry,
} from './lexicon-model'
import {
  entryParadigm,
  entrySenses,
  getPinnedCells,
  translationFor,
  validateEntry,
} from './lexicon-model'
import { InlineExampleEditor } from './inline-example-editor'

const CEFR_LEVELS: Cefr[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']

export interface PurgeOptions {
  /** this browser's own copy */
  local: boolean
  /**
   * The shared entry, every model response cached for it, and its generated
   * media. Media goes with it: regeneration writes new prompts under new keys,
   * so anything left behind is orphaned rather than reused.
   */
  remote: boolean
}

type EntryEditorProps =
  | {
      spaceId: string
      mode: 'adopted'
      entry: LexiconEntry
      l1: TranslationLanguage
      onChange: (next: LexiconEntry, current: LexiconEntry) => void
      readOnly?: boolean
      generationRefused?: boolean
      deviceId?: string
      rulePageExists?: boolean
      onOpenRulePage?: () => void
      onDelete?: (options?: PurgeOptions) => void | Promise<void>
      cardStates?: Record<string, { frozen: boolean; deleted: boolean }>
      onSetCardFrozen?: (qid: string, frozen: boolean) => void | Promise<void>
      onDeleteCard?: (qid: string) => void | Promise<void>
      onRequestCard?: (qid: string) => void | Promise<void>
      resolveMediaSrc?: (src: string) => string
    }
  | {
      spaceId: string
      mode: 'preview'
      entry: CachedEntry
      l1: TranslationLanguage
      onAdopt?: (selectedSenseIds: string[]) => void | Promise<void>
      // A bad generation is judged from the preview, so purging must be
      // reachable here — not only after adopting it just to delete it.
      onDelete?: (options?: PurgeOptions) => void | Promise<void>
      readOnly?: boolean
      rulePageExists?: boolean
      onOpenRulePage?: () => void
      resolveMediaSrc?: (src: string) => string
    }

export function EntryEditor(props: EntryEditorProps) {
  const [warningNode, setWarningNode] = useState<string | null>(null)
  const allowEdit = (nodeId: string, example: Example) => {
    if (props.mode !== 'adopted' || !props.generationRefused || (!example.imageSrc && !example.nodeAudioSrc)) return true
    const key = `lexicon-drop-ack:${props.deviceId}:${props.entry.id}:${nodeId}`
    // Blocked site data throws from the localStorage getter; the ack then holds for this page load.
    if (localStorageOrMemory().getItem(key)) return true
    setWarningNode(nodeId)
    return false
  }
  const { entry } = props
  const l1 = props.l1
  // Senses and paradigm are keyed maps on an adopted entry (they CRDT merge).
  // Every render path iterates them through the canonical order helpers, so the
  // editor never depends on key insertion order.
  const senses = entrySenses(entry)
  const forms = entry.kind === 'lexeme' ? entryParadigm(entry) : []
  const [selected, setSelected] = useState(() => new Set<string>())
  const [adopting, setAdopting] = useState(false)
  const [adoptError, setAdoptError] = useState<unknown>(null)
  const editable = props.mode === 'adopted' && !props.readOnly
  const entryTranslation =
    translationFor(props.entry.translations, props.l1) ||
    entrySenses(props.entry)[0]?.gloss ||
    props.entry.headword

  return (
    <article
      // Preview sits inside Vocabulary's padded column; only the adopted page owns its horizontal inset (16px mobile).
      // gap-8 sets 32px between article sections and between senses.
      className={`flex w-full flex-col gap-8 pb-12 ${props.mode === 'preview' ? '' : 'px-4 md:px-6'}`}
      data-testid={
        props.mode === 'preview'
          ? 'lexicon-entry-preview'
          : 'lexicon-entry-editor'
      }
    >
      {warningNode ? (
        <div role="alert">
          Generated material may be permanently lost; generation unavailable.
          <Button data-testid="entry-confirm-drop" onClick={() => {
            if (props.mode === 'adopted') localStorageOrMemory().setItem(`lexicon-drop-ack:${props.deviceId}:${entry.id}:${warningNode}`, '1')
            setWarningNode(null)
          }}>Continue editing</Button>
          <Button onClick={() => { setWarningNode(null) }}>Cancel</Button>
        </div>
      ) : null}
      <header className="border-separator flex flex-col gap-4 border-b pb-6">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            {entry.audioSrc ? (
              <div data-testid="entry-headword-audio">
                <AudioPlayer
                  src={_mediaSrc(entry.audioSrc, props.resolveMediaSrc)}
                  minimal
                />
              </div>
            ) : null}
            <h1 className="text-on-surface min-w-0 font-serif text-4xl font-semibold tracking-tight normal-case md:text-5xl">
              {entry.headword}
            </h1>
          </div>
          {props.onDelete ? (
            <DeleteSplitButton onDelete={props.onDelete} scope={props.mode} />
          ) : null}
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-auto flex-wrap items-baseline gap-2">
            <Field
              ariaLabel="Transcription"
              className="text-on-surface-secondary inline-flex w-auto max-w-full flex-none font-serif text-lg [&_[data-slot=input]]:field-sizing-content [&_[data-slot=input]]:w-auto"
              value={entry.transcription}
              editable={editable}
              testId="entry-transcription"
              onCommit={(value) => {
                if (props.mode === 'adopted') {
                  _commit(
                    props.entry,
                    { ...props.entry, transcription: value },
                    props.onChange,
                  )
                }
              }}
            />
            <span
              aria-hidden={true}
              className="text-on-surface-secondary shrink-0"
            >
              ·
            </span>
            <p className="text-on-surface-secondary min-w-0 text-sm">
              {entryTranslation}
            </p>
          </div>
          <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2">
            <Chip>
              {entry.kind === 'lexeme' ? entry.pos : entry.kind}
            </Chip>
            {entry.kind === 'lexeme' &&
              (props.rulePageExists && props.onOpenRulePage ? (
                <button
                  className="border-border text-primary hover:bg-surface-2 shrink-0 rounded-full border px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors"
                  type="button"
                  onClick={props.onOpenRulePage}
                >
                  {entry.inflectionClass}
                </button>
              ) : (
                <Chip>{entry.inflectionClass}</Chip>
              ))}
          </div>
        </div>
      </header>

      <section className="flex flex-col gap-8">
        {senses.map((sense, index) => {
          const selectable = props.mode === 'preview' && !props.readOnly
          const senseRecallState = props.mode === 'adopted' && sense.senseRecallQuestionId ? props.cardStates?.[sense.senseRecallQuestionId] : undefined
          const formRecallState = props.mode === 'adopted' && sense.formRecallQuestionId ? props.cardStates?.[sense.formRecallQuestionId] : undefined
          const senseSelected = props.mode === 'preview' ? selected.has(sense.id)
            : Boolean(senseRecallState && !senseRecallState.deleted && formRecallState && !formRecallState.deleted)

          const selectionProps = selectable
            ? {
                'aria-checked': selected.has(sense.id),
                'aria-label': `Select ${sense.gloss}`,
                role: 'checkbox' as const,
                tabIndex: 0,
                onClick: (event: React.MouseEvent<HTMLElement>) => {
                  if (_isInteractiveTarget(event.target)) return
                  setSelected((current) =>
                    _toggleSelection(current, sense.id),
                  )
                },
                onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
                  if (
                    event.target !== event.currentTarget ||
                    (event.key !== 'Enter' && event.key !== ' ')
                  )
                    return
                  event.preventDefault()
                  setSelected((current) =>
                    _toggleSelection(current, sense.id),
                  )
                },
              }
            : {}
          const translation =
            translationFor(sense.translations, props.l1) || sense.gloss
          const imageSrc = sense.example.imageSrc
            ? _mediaSrc(sense.example.imageSrc, props.resolveMediaSrc)
            : ''
          return (
            <section
              key={sense.id}
              className={`grid gap-5 transition-[padding] duration-100 data-selected:rounded-[24px]! data-selected:bg-surface-2 data-selected:p-4 md:grid-cols-[minmax(0,1fr)_14rem] md:gap-8 ${selectable ? 'cursor-pointer' : ''}`}
              data-testid={`entry-sense-${sense.id}`}
              data-selected={senseSelected || undefined}
              {...selectionProps}
            >
              <div className="flex min-w-0 flex-col gap-4">
              <div className="flex min-w-0 items-start gap-3">
                {(props.mode === 'preview' && !props.readOnly) || (props.mode === 'adopted' && props.cardStates) ? (
                <div className="flex min-h-7 shrink-0 items-center">
                  {props.mode === 'preview' && !props.readOnly ? (
                    <Checkbox
                      aria-hidden={true}
                      checked={selected.has(sense.id)}
                      data-testid={`lexicon-sense-${sense.id}`}
                      tabIndex={-1}
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                      onCheckedChange={(checked) => {
                        setSelected((current) => {
                          const next = new Set(current)
                          if (checked) next.add(sense.id)
                          else next.delete(sense.id)
                          return next
                        })
                      }}
                    />
                  ) : props.mode === 'adopted' && props.cardStates ? (
                    <SenseLearningCheckbox spaceId={props.spaceId} senseId={sense.id}
                      checked={senseSelected} />
                  ) : null}
                </div>
                ) : null}
                <div className="min-w-0 flex-1">
                {editable ? (
                  <Field
                    ariaLabel={`Definition ${index + 1} gloss`}
                    className="px-0 py-0 font-serif text-xl leading-snug [&_textarea]:min-h-0 [&_textarea]:p-0 [&_textarea]:leading-snug [&_textarea]:break-words"
                    value={sense.gloss}
                    editable
                    multiline
                    testId={`entry-sense-${sense.id}-gloss`}
                    beforeEdit={() => allowEdit(sense.id, sense.example)}
                    onCommit={(value) => {
                      if (props.mode !== 'adopted') return
                      const next = structuredClone(props.entry)
                      next.senses[sense.id].gloss = value
                      _commit(props.entry, next, props.onChange)
                    }}
                  />
                ) : (
                  <h2
                    className="text-on-surface font-serif text-xl leading-snug break-words whitespace-pre-wrap"
                    data-testid={`entry-sense-${sense.id}-gloss`}
                  >
                    {sense.gloss}
                  </h2>
                )}
                </div>
              </div>
                <div className="flex flex-wrap items-center gap-2">
                  {editable ? (
                    <select
                      aria-label={`Definition ${index + 1} level`}
                      className="bg-surface-3 text-on-surface-secondary h-6 rounded-full border-0 px-2 text-xs font-medium outline-none"
                      value={sense.cefr}
                      onChange={(event) => {
                        if (props.mode !== 'adopted') return
                        const next = structuredClone(props.entry)
                        next.senses[sense.id].cefr = event.target.value as Cefr
                        _commit(props.entry, next, props.onChange)
                      }}
                    >
                      {CEFR_LEVELS.map((level) => (
                        <option key={level}>{level}</option>
                      ))}
                    </select>
                  ) : (
                    <Chip>{sense.cefr}</Chip>
                  )}
                  {sense.labels.map((label) => (
                    <Chip key={label}>{label}</Chip>
                  ))}
                  {editable && senses.length > 1 ? (
                    <IconButton
                      aria-label={`Remove Definition ${index + 1}`}
                      className="ml-auto"
                      size="sm"
                      variant="text"
                      onClick={() => {
                        if (props.mode !== 'adopted') return
                        const next = structuredClone(props.entry)
                        const { [sense.id]: _removed, ...rest } = next.senses
                        next.senses = rest
                        _commit(props.entry, next, props.onChange)
                      }}
                      data-testid={`entry-sense-${sense.id}-remove`}
                    >
                      <XIcon /> Remove definition
                    </IconButton>
                  ) : null}
                </div>
                {_distinctTranslation(
                  translation,
                  sense.gloss,
                  entry.headword,
                ) ? (
                  <Field
                    ariaLabel={`Definition ${index + 1} translation`}
                    className="text-on-surface-secondary text-sm"
                    value={translation}
                    editable={editable}
                    testId={`entry-sense-${sense.id}-translation`}
                    beforeEdit={() => allowEdit(sense.id, sense.example)}
                    onCommit={(value) => {
                      if (props.mode !== 'adopted') return
                      const next = structuredClone(props.entry)
                      next.senses[sense.id].translations[props.l1] = value
                      _commit(props.entry, next, props.onChange)
                    }}
                  />
                ) : null}
                <ExampleField
                  spaceId={props.spaceId}
                  example={sense.example}
                  l1={l1}
                  editable={editable}
                  needle={entry.headword}
                  ariaLabel={`Definition ${index + 1} example`}
                  testId={`entry-sense-${sense.id}-example`}
                  beforeEdit={() => allowEdit(sense.id, sense.example)}
                  onCommit={(example) => {
                    if (props.mode !== 'adopted') return false
                    const next = structuredClone(props.entry)
                    next.senses[sense.id].example = example
                    return _commit(props.entry, next, props.onChange)
                  }}
                />
                {sense.example.nodeAudioSrc ? (
                  <div data-testid={`entry-sense-${sense.id}-audio`}>
                    <AudioPlayer
                      src={_mediaSrc(
                        sense.example.nodeAudioSrc,
                        props.resolveMediaSrc,
                      )}
                      minimal
                    />
                  </div>
                ) : null}
                <CardStrip
                  entry={props.mode === 'adopted' ? props.entry : undefined}
                  qids={[
                    sense.senseRecallQuestionId,
                    sense.formRecallQuestionId,
                  ].filter((qid): qid is string => qid !== undefined)}
                  states={
                    props.mode === 'adopted' ? props.cardStates : undefined
                  }
                  onSetFrozen={
                    props.mode === 'adopted' ? props.onSetCardFrozen : undefined
                  }
                  onDelete={
                    props.mode === 'adopted' ? props.onDeleteCard : undefined
                  }
                />
              </div>
              <div>
                {sense.example.imageSrc ? (
                  <div data-testid={`entry-sense-${sense.id}-image`}>
                    <Image
                      className="aspect-square w-full rounded-2xl"
                      cover
                      noThumb
                      src={imageSrc}
                      alt={`Illustration for ${entry.headword}`}
                    />
                  </div>
                ) : (
                  <div className="border-separator bg-surface-2 aspect-square w-full rounded-2xl border border-dashed" />
                )}
              </div>
            </section>
          )
        })}
      </section>

      {editable ? (
        <Button
          className="w-full"
          data-testid="entry-add-sense"
          variant="filled"
          onClick={() => {
            if (props.mode !== 'adopted') return
            const next = structuredClone(props.entry)
            const senseId = ulid()
            next.senses[senseId] = {
              id: senseId,
              gloss: 'New definition',
              translations: { [props.l1]: '' },
              cefr: 'A1',
              labels: [],
              example: {
                text: `Example with ${entry.headword}.`,
                node: [13, entry.headword.length],
                translations: { [props.l1]: '' },
              },
              senseRecallQuestionId: ulid(),
              formRecallQuestionId: ulid(),
            }
            _commit(props.entry, next, props.onChange)
          }}
        >
          <PlusIcon /> Add definition
        </Button>
      ) : null}

      {entry.kind === 'lexeme' ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-on-surface font-serif text-2xl font-semibold">
            Paradigm
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {forms.map((form, index) => (
              <div
                key={form.cell}
                className="border-separator flex min-w-0 flex-col gap-4 rounded-xl border p-4"
              >
                <div className="flex min-w-0 flex-col gap-1.5">
                <h3 className="text-on-surface-secondary text-xs font-medium break-words">
                  {form.cell}
                </h3>
                <div className="flex w-full min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <Field
                  ariaLabel={`${form.cell} form`}
                  className="min-w-0 font-serif text-lg font-semibold break-words"
                  value={form.form}
                  editable={editable}
                  testId={`entry-paradigm-${index}-form`}
                  beforeEdit={() => allowEdit(form.cell, form.example)}
                  onCommit={(value) => {
                    if (
                      props.mode !== 'adopted' ||
                      props.entry.kind !== 'lexeme'
                    )
                      return
                    const next = structuredClone(props.entry)
                    next.paradigm[form.cell].form = value
                    _commit(props.entry, next, props.onChange)
                  }}
                />
                <Field
                  ariaLabel={`${form.cell} transcription`}
                  className="text-on-surface-secondary ml-auto min-w-0 text-right text-sm break-words"
                  value={form.transcription}
                  editable={editable}
                  testId={`entry-paradigm-${index}-transcription`}
                  beforeEdit={() => allowEdit(form.cell, form.example)}
                  onCommit={(value) => {
                    if (
                      props.mode !== 'adopted' ||
                      props.entry.kind !== 'lexeme'
                    )
                      return
                    const next = structuredClone(props.entry)
                    next.paradigm[form.cell].transcription = value
                    _commit(props.entry, next, props.onChange)
                  }}
                />
                </div>
                </div>
                <div className="flex min-w-0 flex-col gap-2">
                  <ExampleField
                  spaceId={props.spaceId}
                    example={form.example}
                    l1={l1}
                    editable={editable}
                    needle={form.form}
                    ariaLabel={`${form.cell} example`}
                    testId={`entry-paradigm-${index}-example`}
                    beforeEdit={() => allowEdit(form.cell, form.example)}
                    secondary
                    onCommit={(example) => {
                      if (
                        props.mode !== 'adopted' ||
                        props.entry.kind !== 'lexeme'
                      )
                        return false
                      const next = structuredClone(props.entry)
                      next.paradigm[form.cell].example = example
                      return _commit(props.entry, next, props.onChange)
                    }}
                  />
                  {editable ? (
                    <div className="flex items-center justify-between gap-2 pt-2">
                      <CardStrip
                        entry={
                          props.mode === 'adopted' ? props.entry : undefined
                        }
                        qids={
                          form.formRecallQuestionId
                            ? [form.formRecallQuestionId]
                            : []
                        }
                        states={
                          props.mode === 'adopted'
                            ? props.cardStates
                            : undefined
                        }
                        onSetFrozen={
                          props.mode === 'adopted'
                            ? props.onSetCardFrozen
                            : undefined
                        }
                        onRequest={props.mode === 'adopted' ? props.onRequestCard : undefined}
                        onDelete={
                          props.mode === 'adopted'
                            ? props.onDeleteCard
                            : undefined
                        }
                      />
                      <RemoveFormButton
                        testId={`entry-paradigm-${index}-remove`}
                        onRemove={() => {
                          if (
                            props.mode !== 'adopted' ||
                            props.entry.kind !== 'lexeme'
                          )
                            return
                          const next = structuredClone(props.entry)
                          const { [form.cell]: _dropped, ...rest } =
                            next.paradigm
                          next.paradigm = rest
                          _commit(props.entry, next, props.onChange)
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          {forms.length === 0 && !getPinnedCells(entry.language, entry.pos) ? (
            <p
              className="text-on-surface-secondary text-sm"
              data-testid="entry-paradigm-unpinned"
            >
              no cell vocabulary pinned for {entry.language} {entry.pos} yet
            </p>
          ) : null}
          {editable ? (
            <AddForm
              entry={
                props.mode === 'adopted' && props.entry.kind === 'lexeme'
                  ? props.entry
                  : undefined
              }
              onAdd={(cell) => {
                if (props.mode !== 'adopted' || props.entry.kind !== 'lexeme')
                  return
                const next = structuredClone(props.entry)
                next.paradigm[cell] = {
                  cell,
                  form: entry.headword,
                  transcription: entry.transcription,
                  example: {
                    text: `Example with ${entry.headword}.`,
                    node: [13, entry.headword.length],
                    translations: { [props.l1]: '' },
                  },
                  formRecallQuestionId: ulid(),
                }
                _commit(props.entry, next, props.onChange)
              }}
            />
          ) : null}
        </section>
      ) : null}

      {props.mode === 'preview' && !props.readOnly && props.onAdopt ? (
        selected.size > 0 ? (<>
          <div className="pointer-events-none fixed inset-x-0 bottom-[calc(1.5rem+env(safe-area-inset-bottom))] z-30 flex justify-center md:group-has-[[data-slot=sidebar][data-state=expanded]]/sidebar-wrapper:left-(--sidebar-width)">
            <Fab
              className="pointer-events-auto shadow-lg"
              data-testid="lexicon-adopt-entry"
              extended
              loading={adopting}
              onClick={() => {
                setAdoptError(null)
                setAdopting(true)
                void Promise.resolve(props.onAdopt?.([...selected])).catch(setAdoptError).finally(() => {
                  setAdopting(false)
                })
              }}
            >
              <BookMarkedIcon /> {selected.size === 1 ? 'Adopt' : selected.size === senses.length ? 'Adopt all' : `Adopt ${selected.size}`}
            </Fab>
          </div>
          {adoptError ? <ErrorDetails error={adoptError} compact /> : null}
        </>) : null
      ) : null}
    </article>
  )
}

function SenseLearningCheckbox(props: { spaceId: string; senseId: string; checked: boolean }) {
  const ctx = useRouterContext()
  const { pageId } = useParams({ from: '/$workspaceId/spaces/$spaceId/pages/$pageId' })
  const [error, setError] = useState<unknown>(null)
  const [pending, setPending] = useState<{ checked: boolean } | null>(null)
  const writes = useRef(Promise.resolve())
  return <>
    <Checkbox checked={pending?.checked ?? props.checked} aria-label="Learn this definition"
      data-testid={`lexicon-sense-${props.senseId}`}
      onCheckedChange={checked => {
        const change = { checked }
        setPending(change)
        setError(null)
        writes.current = writes.current.then(() => setSenseLearning(ctx, props.spaceId, pageId, props.senseId, checked))
          .catch(setError).finally(() => { setPending(current => current === change ? null : current) })
      }} />
    {error ? <ErrorDetails error={error} compact /> : null}
  </>
}

function _isInteractiveTarget(target: EventTarget) {
  return (
    target instanceof Element &&
    target.closest('button, input, a, select, textarea, [role="button"]') !==
      null
  )
}

function _toggleSelection(current: Set<string>, senseId: string) {
  const next = new Set(current)
  if (next.has(senseId)) next.delete(senseId)
  else next.add(senseId)
  return next
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-surface-3 text-on-surface-secondary shrink-0 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap">
      {children}
    </span>
  )
}

function Field(props: {
  beforeEdit?: () => boolean
  ariaLabel: string
  value: string
  editable: boolean
  multiline?: boolean
  testId: string
  className?: string
  onCommit: (value: string) => void
}) {
  const [editing, setEditing] = useState<{ initial: string; draft: string } | null>(null)
  const commit = () => {
    if (editing && editing.draft !== editing.initial) props.onCommit(editing.draft)
    setEditing(null)
  }
  const handleKeyDown = useEditorKeys({ onEnter: commit, blurOnEnter: true })
  if (!props.editable) {
    return (
      <p
        className={`text-on-surface whitespace-pre-wrap ${props.className ?? ''}`}
        data-testid={props.testId}
      >
        {props.value}
      </p>
    )
  }
  return (
    <Input
      minimal
      multiline={props.multiline}
      aria-label={props.ariaLabel}
      className={`hover:bg-surface-2 has-focus-visible:bg-surface-2 rounded-md px-1 transition-colors ${props.className ?? ''}`}
      data-testid={props.testId}
      value={editing?.draft ?? props.value}
      onFocus={() => { setEditing({ initial: props.value, draft: props.value }) }}
      onChange={(event) => {
        if (props.beforeEdit && !props.beforeEdit()) return
        const draft = event.target.value
        setEditing(current => ({ initial: current?.initial ?? props.value, draft }))
      }}
      onBlur={() => {
        commit()
      }}
      onKeyDown={handleKeyDown}
    />
  )
}

function ExampleField(props: {
  beforeEdit?: () => boolean
  spaceId: string
  example: Example
  l1: TranslationLanguage
  editable: boolean
  needle: string
  ariaLabel: string
  testId: string
  secondary?: boolean
  onCommit: (example: Example) => boolean
}) {
  const [editing, setEditing] = useState(false)
  return (
    <div className="flex flex-col gap-1">
      {props.editable && editing ? (
        <InlineExampleEditor
          {...props}
          onCancel={() => {
            setEditing(false)
          }}
          onCommit={(example) => {
            const committed = props.onCommit(example)
            if (committed) setEditing(false)
            return committed
          }}
        />
      ) : props.editable ? (
        <button
          aria-label={`Edit ${props.ariaLabel}`}
          className={`hover:bg-surface-2 rounded-md px-1 text-left transition-colors ${props.secondary ? 'text-on-surface-secondary text-sm leading-relaxed' : 'text-sm leading-relaxed'}`}
          data-testid={`${props.testId}-display`}
          type="button"
          onClick={() => {
            if (props.beforeEdit && !props.beforeEdit()) return
            setEditing(true)
          }}
        >
          <Underlined example={props.example} />
          <ExampleTranslation {...props} />
        </button>
      ) : (
        <span>
          <Underlined example={props.example} secondary={props.secondary} />
          <ExampleTranslation {...props} />
        </span>
      )}
    </div>
  )
}

/** Reads inline after the example as ` (translation)`, like every gloss (cbe-ux-inline-gloss); never on a line beneath. */
function ExampleTranslation(props: {
  example: Example
  l1: TranslationLanguage
  testId: string
}) {
  const translation = translationFor(props.example.translations, props.l1)
  return translation ? (
    <span
      className="text-on-surface-secondary text-sm leading-relaxed"
      data-testid={`${props.testId}-translation`}
    >
      {` (${translation})`}
    </span>
  ) : null
}

function Underlined({
  example,
  secondary,
}: {
  example: Example
  secondary?: boolean
}) {
  const [offset, length] = example.node
  return (
    <span
      className={
        secondary
          ? 'text-on-surface-secondary text-sm leading-relaxed'
          : 'text-on-surface text-sm leading-relaxed'
      }
    >
      {example.text.slice(0, offset)}
      <span className="decoration-primary underline decoration-2 underline-offset-4">
        {example.text.slice(offset, offset + length)}
      </span>
      {example.text.slice(offset + length)}
    </span>
  )
}

function DeleteSplitButton(props: {
  onDelete: (options?: PurgeOptions) => void | Promise<void>
  scope: 'adopted' | 'preview'
}) {
  const [confirming, setConfirming] = useState(false)
  const [purge, setPurge] = useState<PurgeOptions>({
    local: true,
    remote: false,
  })

  return (
    <div className="ml-auto inline-flex items-center">
      <Button
        className="rounded-r-none pr-4"
        data-testid={
          confirming ? 'lexicon-delete-confirm' : 'lexicon-delete-entry'
        }
        semanticRole="error"
        size="sm"
        variant="outlined"
        onClick={() => {
          if (confirming) void props.onDelete()
          else setConfirming(true)
        }}
      >
        {confirming ? 'Confirm delete' : 'Delete'}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={(triggerProps) => (
            <IconButton
              {...triggerProps}
              aria-label="Delete options"
              className="rounded-l-none border-l-0"
              data-testid="lexicon-delete-purge-open"
              semanticRole="error"
              size="sm"
              variant="outlined"
            >
              <ChevronDownIcon />
              Delete options
            </IconButton>
          )}
        />
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuGroup>
            <DropdownMenuLabel>Delete + clean generated data</DropdownMenuLabel>
            <PurgeMenuCheck
              label="Local copy"
              checked={purge.local}
              onChange={(local) => {
                setPurge({ ...purge, local })
              }}
            />
            <PurgeMenuCheck
              testId="lexicon-delete-purge-remote"
              label="Remote entry, model cache and media"
              checked={purge.remote}
              onChange={(remote) => {
                setPurge({ ...purge, remote })
              }}
            />
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="lexicon-delete-purge-confirm"
            variant="error"
            onClick={() => {
              void props.onDelete(purge)
            }}
          >
            Delete and purge
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {confirming ? (
        <Button
          className="ml-1"
          data-testid="lexicon-delete-cancel"
          semanticRole="secondary"
          size="sm"
          variant="text"
          onClick={() => {
            setConfirming(false)
          }}
        >
          Cancel
        </Button>
      ) : null}
    </div>
  )
}

function PurgeMenuCheck(props: {
  testId?: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <DropdownMenuCheckboxItem
      data-testid={props.testId}
      checked={props.checked}
      closeOnClick={false}
      onCheckedChange={props.onChange}
    >
      {props.label}
    </DropdownMenuCheckboxItem>
  )
}

function RemoveFormButton(props: { testId: string; onRemove: () => void }) {
  const [confirming, setConfirming] = useState(false)
  return (
    <Tooltip>
      <TooltipTrigger
        render={(triggerProps) => (
          <IconButton
            {...triggerProps}
            aria-label="Remove form"
            data-testid={props.testId}
            semanticRole="error"
            size="sm"
            variant={confirming ? 'outlined' : 'text'}
            onClick={() => {
              if (confirming) props.onRemove()
              else setConfirming(true)
            }}
          >
            <Trash2Icon />
            {confirming ? 'Confirm remove form' : 'Remove form'}
          </IconButton>
        )}
      />
      <TooltipPopup>
        {confirming ? 'Click again to confirm' : 'Remove form'}
      </TooltipPopup>
    </Tooltip>
  )
}

function CardStrip(props: {
  entry?: LexiconEntry
  qids: string[]
  states?: Record<string, { frozen: boolean; deleted: boolean }>
  onSetFrozen?: (qid: string, frozen: boolean) => void | Promise<void>
  onDelete?: (qid: string) => void | Promise<void>
  onRequest?: (qid: string) => void | Promise<void>
}) {
  if (!props.entry || props.qids.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1.5" aria-label="Flashcards">
      {props.qids.map((qid, index) => {
        const state = props.states?.[qid]
        if (!state) return props.onRequest ? <button key={qid} type="button" data-testid={`entry-request-card-${qid}`} onClick={() => { void props.onRequest?.(qid) }}>Add card</button> : null
        return (
          <div
            key={qid}
            className="border-separator bg-surface-1 flex items-center gap-1 rounded-full border pl-2 text-[11px]"
            data-flashcard-qid={qid}
            data-testid={`entry-card-${qid}`}
          >
            <span className="sr-only">Card {index + 1}</span>
            {state?.deleted ? (
              <span className="text-error pr-2">deleted</span>
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger
                    render={(triggerProps) => (
                      <button
                        {...triggerProps}
                        data-testid={`entry-card-freeze-${qid}`}
                        className="hover:bg-surface-3 rounded-full px-1.5 py-1"
                        type="button"
                        onClick={() => {
                          void props.onSetFrozen?.(qid, !state?.frozen)
                        }}
                      >
                        Card · {state?.frozen ? 'Unfreeze' : 'Freeze'}
                      </button>
                    )}
                  />
                  <TooltipPopup>
                    hidden from training until unfrozen
                  </TooltipPopup>
                </Tooltip>
                <button
                  aria-label={`Delete card ${qid}`}
                  className="hover:bg-error/10 text-error rounded-full p-1"
                  type="button"
                  onClick={() => {
                    void props.onDelete?.(qid)
                  }}
                >
                  <Trash2Icon className="size-3" />
                </button>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

function AddForm(props: {
  entry?: Extract<LexiconEntry, { kind: 'lexeme' }>
  onAdd: (cell: string) => void
}) {
  const [cell, setCell] = useState('')
  if (!props.entry) return null
  const pinned = getPinnedCells(props.entry.language, props.entry.pos) ?? []
  const used = new Set(Object.keys(props.entry.paradigm))
  const available = pinned.filter((candidate) => !used.has(candidate))
  if (available.length === 0) return null
  return (
    <div className="flex items-center gap-2">
      <select
        aria-label="Paradigm cell"
        data-testid="entry-add-form-cell"
        className="border-separator bg-surface-1 h-8 rounded-md border px-2 text-xs"
        value={cell}
        onChange={(event) => {
          setCell(event.target.value)
        }}
      >
        <option value="">Choose missing cell…</option>
        {available.map((candidate) => (
          <option key={candidate}>{candidate}</option>
        ))}
      </select>
      <Button
        data-testid="entry-add-form"
        size="sm"
        variant="outlined"
        disabled={!cell}
        onClick={() => {
          props.onAdd(cell)
          setCell('')
        }}
      >
        <PlusIcon /> Add form
      </Button>
    </div>
  )
}

function _commit(
  current: LexiconEntry,
  next: LexiconEntry,
  onChange: (next: LexiconEntry, current: LexiconEntry) => void,
): boolean {
  if (next === current || validateEntry(next).length > 0) return false
  onChange(Object.assign(next, { headwordKey: normalizeLexiconTrigramText(next.headword).trim() }), current)
  return true
}

function _mediaSrc(
  src: string,
  resolve: ((src: string) => string) | undefined,
) {
  if (!resolve) return src
  try {
    return resolve(src)
  } catch {
    return src
  }
}

function _distinctTranslation(
  translation: string,
  gloss: string,
  headword: string,
): boolean {
  const normalized = translation.trim().toLocaleLowerCase()
  return (
    normalized.length > 0 &&
    normalized !== gloss.trim().toLocaleLowerCase() &&
    normalized !== headword.trim().toLocaleLowerCase()
  )
}
